import WandbLoader from '@wandb/weave/common/components/WandbLoader';
import * as Panel2 from '@wandb/weave/components/Panel2/panel';
import {
  opArtifactId,
  opArtifactProject,
  opDict,
  opEntityName,
  opProjectEntity,
  opProjectInternalId,
  opProjectName,
} from '@wandb/weave/core';
import {useValue} from '@wandb/weave/react';
import * as React from 'react';
import {RefObject, useContext, useState} from 'react';

import {
  useFetchArtifactCollectionTriggersQuery,
  useFetchProjectTriggersQuery,
} from '../../../../generated/graphql';
import {
  PremiumFeatureTooltip,
  useIsAutomationEnabled,
} from '../../../../util/accounts/pricing';
import {propagateErrorsContext} from '../../../../util/errors';
import {trackCreateTriggerClicked} from '../../../../util/navigation';
import {isUserVisibleTrigger} from '../../../../util/triggers';
import {maybePluralize} from '../../../../util/uihelpers';
import {CreateTriggerDrawerForCollection} from '../../../ArtifactCollectionSettings/CreateTriggerModal';
import {useIsProjectReadOnly} from '../../../Registries/common/hooks';
import {AccountSelectorContext} from '../../../Search/SearchNav/AccountSelectorContextProvider';
import * as S from './style';

const inputType = 'artifact' as const;
export type ConfigType = {};
type TriggersOverviewProps = {
  inputRef: RefObject<HTMLDivElement> | null;
};

export const TriggersOverview: React.FC<
  Panel2.PanelProps<typeof inputType, ConfigType> & TriggersOverviewProps
> = props => {
  const {selectedAccount} = useContext(AccountSelectorContext);
  const hasAutomationAccess = useIsAutomationEnabled(selectedAccount);
  const [addTriggerModalOpen, setAddTriggerModalOpen] = useState(false);
  const artifactNode = props.input;
  const overviewValue = useValue(
    React.useMemo(() => {
      const projectNode = opArtifactProject({
        artifact: artifactNode,
      });
      return opDict({
        collectionID: opArtifactId({
          artifact: artifactNode,
        }),
        entityName: opEntityName({
          entity: opProjectEntity({project: projectNode}),
        }),
        projectName: opProjectName({
          project: projectNode,
        }),
        projectID: opProjectInternalId({
          project: projectNode,
        }),
      } as any);
    }, [artifactNode])
  );
  const projectTriggersQuery = useFetchProjectTriggersQuery({
    variables: {
      projectName: overviewValue?.result?.projectName,
      entityName: overviewValue?.result?.entityName,
    },
    skip: overviewValue.loading || !overviewValue.result,
    context: propagateErrorsContext(),
  });
  const collectionTriggersQuery = useFetchArtifactCollectionTriggersQuery({
    variables: {
      artifactCollectionID: overviewValue?.result?.collectionID,
    },
    skip: overviewValue.loading || !overviewValue.result,
    context: propagateErrorsContext(),
  });
  const {isProjectReadOnly: readOnly, loading: isProjectReadOnlyLoading} =
    useIsProjectReadOnly(
      overviewValue.result?.entityName ?? '',
      overviewValue.result?.projectName ?? ''
    );
  if (
    overviewValue.loading ||
    overviewValue.result == null ||
    projectTriggersQuery.loading ||
    collectionTriggersQuery.loading ||
    isProjectReadOnlyLoading
  ) {
    return (
      <WandbLoader
        name="artifact-collection-triggers-list"
        inline
        size="tiny"
      />
    );
  }

  const projectScopedTriggers =
    projectTriggersQuery?.data?.project?.triggers
      .filter(t => t.scope.__typename === 'Project')
      .filter(
        t =>
          t.triggeredAction.__typename === 'QueueJobTriggeredAction' ||
          t.triggeredAction.__typename === 'GenericWebhookTriggeredAction'
      ) ?? [];
  const acScopedTriggers =
    collectionTriggersQuery?.data?.artifactCollection?.triggers.edges
      .map(t => {
        return t?.node;
      })
      .filter(
        t =>
          t.triggeredAction.__typename === 'QueueJobTriggeredAction' ||
          t.triggeredAction.__typename === 'GenericWebhookTriggeredAction'
      ) ?? [];
  const triggers = [...acScopedTriggers, ...projectScopedTriggers].filter(
    isUserVisibleTrigger
  );

  return (
    <>
      {triggers!.length === 0 ? (
        <>
          <S.SettingsOverview>
            <S.SettingsOverviewText>
              Automate actions based on changes in this registered model.
            </S.SettingsOverviewText>
            {!readOnly && (
              <S.ClickableText
                onClick={() => {
                  if (hasAutomationAccess) {
                    setAddTriggerModalOpen(true);
                    trackCreateTriggerClicked(
                      'Model registry collection overview section'
                    );
                  }
                }}>
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <span style={{marginRight: '5px'}}>Create automation</span>
                  <PremiumFeatureTooltip
                    isPremiumFeatureDisabled={!hasAutomationAccess}
                  />
                </div>
              </S.ClickableText>
            )}
          </S.SettingsOverview>
          {addTriggerModalOpen && (
            <CreateTriggerDrawerForCollection
              open={addTriggerModalOpen}
              onClose={() => setAddTriggerModalOpen(false)}
              projectName={overviewValue.result.projectName}
              entityName={overviewValue.result.entityName}
              projectID={overviewValue.result.projectID}
              artifactCollectionID={overviewValue.result.collectionID}
              artifactCollectionName={overviewValue.result.collectionName}
            />
          )}
        </>
      ) : (
        <S.SettingsOverview>
          <S.ClickableText
            onClick={() => {
              props.inputRef?.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
              });
            }}>
            <S.StyledIcon name="trigger-alt" />
            {maybePluralize(triggers?.length, 'automation')}
          </S.ClickableText>
        </S.SettingsOverview>
      )}
    </>
  );
};
